/** @jsxImportSource @emotion/react */
'use memo'

import { useState } from 'react'
import styled from '@emotion/styled'
import { useOrgRegistrations } from '@sportninja/common/features/organization/registration/hooks/useOrgRegistrations'
import { useOrgVenueOptions } from '@sportninja/common/features/organization/registration/hooks/useOrgVenueOptions'
import type { OrgRegistration } from '@sportninja/common/features/organization/registration/types'
import { useSportSelectOptions } from 'src/hooks/useSportSelectOptions'
import { useOrgRegistrationUrlFilters } from '../hooks/useOrgRegistrationUrlFilters'
import type { OrgRegistrationView } from '../types'
import OrgRegistrationFilters from './OrgRegistrationFilters'
import OrgRegistrationList from './OrgRegistrationList'
import OrgRegistrationListView from './list/OrgRegistrationListView'

interface Props {
  /** The organization (or sub-organization) whose page this tab is on. */
  orgId: string
}

/**
 * The organization page's Registrations tab: this organization's registrations
 * — and its sub-organizations' when switched on — filtered by search, sport,
 * location and status, all held in the URL so the view can be shared as a link.
 * Shown as cards or as one sortable table; the choice starts on cards every
 * visit.
 */
const OrgRegistrations = ({ orgId }: Props) => {
  'use memo'

  const {
    filters,
    setStatus,
    setSearch,
    setSport,
    setVenue,
    setShowChildOrganizations,
  } = useOrgRegistrationUrlFilters()
  const sportSelect = useSportSelectOptions()
  const venues = useOrgVenueOptions(orgId)
  const list = useOrgRegistrations(orgId, filters)
  const [view, setView] = useState<OrgRegistrationView>('cards')

  // A basic-stats pick filters by its own id alone; a full-stats pick by the
  // sport id. Only one sport is ever applied.
  const changeSport = (value: string) => {
    const selection = sportSelect.resolve(value)
    setSport({
      sportId: selection && !selection.isGeneric ? selection.value : '',
      genericSportId: selection?.isGeneric ? selection.value : '',
    })
  }

  // A new tab, so the admin keeps their filtered list.
  const openRegistration = (registration: OrgRegistration) =>
    window.open(
      `/registration/${registration.scheduleId}`,
      '_blank',
      'noopener,noreferrer'
    )

  return (
    <Wrapper>
      <OrgRegistrationFilters
        filters={filters}
        sportGroups={sportSelect.groups}
        sportFilterOption={sportSelect.filterOption}
        venueOptions={venues.options}
        venuesDisabled={venues.isLoading || venues.isError}
        onStatusChange={setStatus}
        onSearch={setSearch}
        onSportChange={changeSport}
        onVenueChange={setVenue}
        onShowChildOrganizationsChange={setShowChildOrganizations}
        view={view}
        onViewChange={setView}
      />
      {view === 'list' ? (
        <OrgRegistrationListView
          list={list}
          status={filters.status}
          onOpen={openRegistration}
        />
      ) : (
        <OrgRegistrationList
          list={list}
          status={filters.status}
          onOpen={openRegistration}
        />
      )}
    </Wrapper>
  )
}

export default OrgRegistrations

const Wrapper = styled.section`
  display: flex;
  flex-direction: column;
  gap: 24px;
  /* No side padding — the page container already insets it, and the filters
     and cards line up with the tab bar like the other org tabs. */
  padding-top: 16px;
  /* Room under the last card, so it doesn't sit on the page's bottom edge. */
  padding-bottom: 32px;
`
